import { CardContent } from '@daily/shared/components/Card';
import { AppsIcon } from '@daily/shared/components/Icons/AppsIcon';
import { Popover } from '@daily/shared/components/Popover';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { useTheme } from '@daily/shared/contexts/Theme';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppsList } from '/components/Apps/AppsList';
import { useCustomIntegrations } from '/hooks/useCustomIntegrations';

import { TrayButton } from './TrayButton';

interface Props extends React.ComponentProps<typeof TrayButton> {}

export const AppsControls: React.FC<Props> = ({
  className,
  onClick,
  ...props
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { canControlIntegration, mainIntegrations, runningSharedIntegrations } =
    useCustomIntegrations();
  const apps = mainIntegrations.filter(canControlIntegration);
  const [showPopover, setShowPopover] = useState(false);

  const handleClickApps = useCallback(
    (ev: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(ev);
      setShowPopover(true);
    },
    [onClick]
  );
  const handleClosePopover = useCallback(() => {
    setShowPopover(false);
  }, []);

  if (!apps.length) return null;

  const controllableRunningApps = mainIntegrations
    .filter(canControlIntegration)
    .filter((i) => i.id in runningSharedIntegrations || i.running);
  const isControllableAppRunning = controllableRunningApps.length > 0;

  return (
    <div
      className={classNames('apps-controls', className, {
        active: isControllableAppRunning,
      })}
    >
      <TrayButton
        onClick={handleClickApps}
        {...props}
        id={props?.id ?? 'apps-btn'}
      >
        <AppsIcon
          className="icon"
          color={
            isControllableAppRunning ? colors.system.white : 'currentColor'
          }
        />
        <Text>{t('general.apps')}</Text>
      </TrayButton>
      {showPopover && (
        <Popover
          align="right"
          aria-labelledby={props?.id ?? 'apps-btn'}
          id="apps-popover"
          onClose={handleClosePopover}
          placement="top"
          cardProps={{
            appearance: 'grey',
            spacing: 8,
          }}
        >
          <CardContent>
            <AppsList
              onClickApp={handleClosePopover}
              iconSize={16}
              iconVariant="thick"
              textVariant="base"
            >
              <Stack align="center" gap={4} horizontal>
                <AppsIcon size={16} />
                <Text variant="strong">{t('general.apps')}</Text>
              </Stack>
            </AppsList>
          </CardContent>
        </Popover>
      )}
      <style jsx>{`
        .apps-controls img {
          display: block;
          margin: 2px;
        }
        .apps-controls :global(.icon) {
          border-radius: 4px;
        }
        .apps-controls.active :global(.icon) {
          background: ${colors.system.red};
        }
      `}</style>
    </div>
  );
};
